import { ref, type Ref } from "vue";
import { captureException } from "@sentry/vue";
import { adminV1OrganizationsPaymentsScheduledPaymentsChangeMethodPartialUpdate } from "@/api/admin-payments-scheduled-payments/admin-payments-scheduled-payments";
import type { ChangePaymentMethodResponse } from "@/api/model";
import { PaymentMethodTypeEnum } from "@/api/model";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";

interface ChangePaymentMethodOptions {
  scheduledPaymentId: string;
  paymentMethodToken: string;
  paymentMethodType: PaymentMethodTypeEnum;
}

interface UseChangePaymentMethodReturn {
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
  result: Ref<ChangePaymentMethodResponse | null>;
  changePaymentMethod: (
    options: ChangePaymentMethodOptions
  ) => Promise<boolean>;
}

export function useChangePaymentMethod(): UseChangePaymentMethodReturn {
  const { currentOrganizationId } = useCurrentOrganization();

  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const result = ref<ChangePaymentMethodResponse | null>(null);

  const changePaymentMethod = async (
    options: ChangePaymentMethodOptions
  ): Promise<boolean> => {
    if (!currentOrganizationId.value) {
      error.value = "Organization not found";
      return false;
    }

    isLoading.value = true;
    error.value = null;

    try {
      result.value =
        await adminV1OrganizationsPaymentsScheduledPaymentsChangeMethodPartialUpdate(
          currentOrganizationId.value,
          options.scheduledPaymentId,
          {
            payment_method_token: options.paymentMethodToken,
            payment_method_type: options.paymentMethodType,
          }
        );

      return true;
    } catch (err: unknown) {
      captureException(err, {
        extra: { message: "Failed to change payment method" },
      });
      error.value = "Failed to change payment method";
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    result,
    changePaymentMethod,
  };
}
